import styled, { css } from 'styled-components';
import { TokenName } from '@mewssystems/design-tokens';
import { getTextTokenPreset, getToken } from '@optimus-web/common';
import { ANIMATION_CURVE, ANIMATION_DURATION } from '@optimus-web/local-tokens';
import { Button } from '../../../Button';
import { Icon } from '../../../Icon';
import { Stack } from '../../../Stack';

const getSelectionBorderRadiusStyles = (
    active: boolean,
    previousIsSelected: boolean,
    nextIsSelected: boolean,
    siblingActiveToken: number,
    onlyActiveToken: number
) => `
    border-radius:
        ${active && previousIsSelected ? `${siblingActiveToken}px` : `${onlyActiveToken}px`}
        ${active && previousIsSelected ? `${siblingActiveToken}px` : `${onlyActiveToken}px`}
        ${active && nextIsSelected ? `${siblingActiveToken}px` : `${onlyActiveToken}px`}
        ${active && nextIsSelected ? `${siblingActiveToken}px` : `${onlyActiveToken}px`};
`;

export const FilterContainer = styled(Stack)`
    padding: ${getToken(TokenName.SPACING_100)}px ${getToken(TokenName.SPACING_200)}px
        ${getToken(TokenName.SPACING_200)}px;
`;

export const FilterLabel = styled.label`
    color: ${getToken(TokenName.TEXT_STATIC_PRIMARY)};
    ${getTextTokenPreset('BODY_M_STRONG')};
`;

export const OptionList = styled.menu`
    padding: ${getToken(TokenName.SPACING_0)};
    margin: ${getToken(TokenName.SPACING_0)};
    list-style: none;
`;

export const OptionListItem = styled.li<{ active: boolean; previousIsSelected: boolean; nextIsSelected: boolean }>`
    padding-left: ${getToken(TokenName.SPACING_100)}px;

    background-color: ${({ active }) =>
        active
            ? getToken(TokenName.BACKGROUND_INTERACTIVE_SECONDARY_DEFAULT)
            : getToken(TokenName.BACKGROUND_INTERACTIVE_NEUTRAL_SUBTLE_DEFAULT)};

    border-bottom: 1px solid
        ${({ active }) =>
            active ? getToken(TokenName.BORDER_STATIC_INVERSE) : getToken(TokenName.BACKGROUND_STATIC_FLOATING)};

    ${({ active, previousIsSelected, nextIsSelected, theme }) =>
        getSelectionBorderRadiusStyles(
            active,
            previousIsSelected,
            nextIsSelected,
            getToken(TokenName.BORDER_RADIUS_0)({ theme }),
            getToken(TokenName.BORDER_RADIUS_100)({ theme })
        )}

    transition-property: background-color;
    transition-timing-function: ${ANIMATION_CURVE.default};
    transition-duration: ${ANIMATION_DURATION.default}ms;

    &:hover {
        background-color: ${({ active }) =>
            active
                ? getToken(TokenName.BACKGROUND_INTERACTIVE_SECONDARY_HOVER)
                : getToken(TokenName.BACKGROUND_INTERACTIVE_NEUTRAL_SUBTLE_HOVER)};
    }
`;

export const OptionListItemLabel = styled.span`
    cursor: pointer;
    padding-right: ${getToken(TokenName.SPACING_100)}px;
`;

export const SortingDefault = styled(Icon)`
    width: ${getToken(TokenName.SPACING_200)}px;
    height: ${getToken(TokenName.SPACING_200)}px;
    margin-left: ${getToken(TokenName.SPACING_50)}px;
    color: transparent;
`;

export const SortingIcon = styled(Icon)`
    margin-left: ${getToken(TokenName.SPACING_50)}px;
    color: ${getToken(TokenName.TEXT_STATIC_PRIMARY)};
`;

export const HeaderButton = styled(Button)<{
    asTag?: string;
    asIconButton?: boolean;
}>`
    padding: ${getToken(TokenName.SPACING_0)};
    ${getTextTokenPreset('BODY_M_STRONG')};
    border: none;

    ${({ asTag }) =>
        asTag === 'span' &&
        css`
            cursor: auto;
        `}

    ${({ asIconButton }) =>
        asIconButton &&
        css`
            &:last-child {
                flex-shrink: 0;
                margin-left: ${getToken(TokenName.SPACING_50)}px;
                height: 24px;
                width: 24px;
            }
        `}

    ${({ asIconButton }) =>
        !asIconButton &&
        css`
            &:hover:not(:disabled) {
                background-color: initial;
                border-color: transparent;
                border-radius: 0;
            }
        `}

    &:hover ${SortingDefault}, &:hover ${SortingIcon} {
        color: ${getToken(TokenName.TEXT_STATIC_TERTIARY)};
    }

    &:active ${SortingDefault}, &:active ${SortingIcon} {
        color: ${getToken(TokenName.TEXT_STATIC_PRIMARY)};
    }
`;

export const HeaderFilterButton = styled(HeaderButton)``;
